'use client'

import React, { useEffect, useRef } from 'react'
import AppStoreLink from '@/app/components/AppStoreLink'
import { track } from '@/utils/analytics'
import type { BlogPostMeta } from '@/app/blog/posts'

/**
 * Above-the-fold answer card: renders the article's own BlogTLDR verdict above
 * the hero image, with the install ask as one line underneath it.
 *
 * The ask must stay below the verdict. It offers the one thing the article
 * structurally cannot — a check on the product in the reader's hand, rather
 * than the one we chose to write about — and that trade only reads as useful
 * once they've actually got their answer.
 *
 * No dismiss button by design: this is the article's verdict, not an overlay.
 *
 * Fires `hero_answer_*`, deliberately NOT the older `hero_app_cta_*`, which is
 * shared by several unrelated components and so cannot be attributed per-CTA.
 */
type Topic = NonNullable<BlogPostMeta['topic']>

/**
 * One line each. The rule: name the reader's own object, not our product. The
 * article covers the products we picked; the app covers the one they are
 * actually holding. That gap is the entire pitch, and it only makes sense
 * *after* they've read the verdict — which is why it lives here and not above.
 */
const TOPIC_ASK: Record<Topic, string> = {
  water: 'Holding a different bottle? Review its confirmed or estimated material and Product Contact Signal, then compare a lower-contact option.',
  food: 'Got a different brand in the cupboard? Review its packaging inputs and Product Contact Signal, then compare a lower-contact option.',
  cosmetics: 'Different product on your shelf? Paste the declared ingredient list to review documented name matches; this is not a product test.',
  pregnancy: 'Different bottle, pouch, or wipe? Review its confirmed or estimated material and compare contact conditions, not safety.',
  home: 'Different container in your kitchen? Review its confirmed or estimated material and Product Contact Signal, then compare a lower-contact option.',
  clothing: 'Different fabric label? Review the declared composition and modeled fibre-contact context; the app does not test the fabric.',
  health: 'Want a saved-scan baseline? Assess products you use often and compare their modeled contact inputs, not bodily exposure.',
  general: 'Wondering about a product we did not cover? Review available product data, confirm the inputs, and read the evidence grade.',
}

export interface HeroAnswerProps {
  topic: Topic
  postSlug?: string
  /** The article's own BlogTLDR element, hoisted up by BlogPostLayout. */
  children: React.ReactNode
}

export default function HeroAnswer({ topic, postSlug, children }: HeroAnswerProps) {
  // One impression per render, same as the old component, so the two surfaces
  // are measured the same way and the comparison is apples-to-apples.
  const impressionFiredRef = useRef(false)
  useEffect(() => {
    if (impressionFiredRef.current) return
    impressionFiredRef.current = true
    track('hero_answer_impression', {
      cta_surface: `blog_hero_answer_${topic}`,
      cta_position: 'hero_answer',
      post_topic: topic,
      ...(postSlug ? { post_slug: postSlug } : {}),
    })
  }, [topic, postSlug])

  const handleCtaClick = () => {
    track('hero_answer_click', {
      cta_surface: `blog_hero_answer_${topic}`,
      cta_position: 'hero_answer',
      post_topic: topic,
      ...(postSlug ? { post_slug: postSlug } : {}),
    })
  }

  const ask = TOPIC_ASK[topic] ?? TOPIC_ASK.general

  return (
    <section
      aria-label="The short answer"
      className="not-prose mb-8 mt-2 rounded-2xl border border-[#1E6B47]/25 bg-white shadow-sm overflow-hidden"
    >
      {/* The verdict. This is the article's own BlogTLDR, hoisted so it lands in
          the first viewport rather than below a 400px hero image. */}
      <div className="p-5 sm:p-6">{children}</div>

      {/* The ask. Visually subordinate by design — a quiet footer strip on the
          answer card, not a competing banner. It should read as a helpful next
          step, because that is what it is. */}
      <div className="border-t border-[#1E6B47]/15 bg-[#1E6B47]/[0.04] px-5 sm:px-6 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
        <p
          className="flex-1 text-sm text-gray-700 leading-snug min-w-0"
          // The copy strings carry HTML entities for apostrophes so they render
          // correctly without fighting the linter's no-unescaped-entities rule.
          dangerouslySetInnerHTML={{ __html: ask }}
        />
        <AppStoreLink
          source={`blog_hero_answer_${topic}`}
          ctaPosition="hero_answer"
          trackingParams={{
            page_type: 'blog',
            post_topic: topic,
            ...(postSlug ? { post_slug: postSlug } : {}),
          }}
          onClick={handleCtaClick}
          className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#1E6B47] text-white text-sm font-semibold px-4 py-2.5 rounded-control hover:bg-[#155235] active:scale-95 transition-[color,background-color,border-color,text-decoration-color,transform] min-h-[44px] pressable"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.08 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
          </svg>
          Scan my product
        </AppStoreLink>
      </div>
    </section>
  )
}

HeroAnswer.displayName = 'HeroAnswer'
